import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { AiAssistantRole } from '../../../core/types/data-models';
import type { FlowStage } from './flowStages';

/** One category card in the flow-progress row: a status dot (filled with a
 * checkmark once every active document type in the category has been
 * generated, outlined with a live count otherwise), the category name, and a
 * done/total pill. Tinted from the category's own AI assistant role `color`
 * (same source/fallback as the AI Assistants blocks below) so a category
 * reads as the same identity here as it does there. */
export function FlowStageCard({ stage, role }: { stage: FlowStage; role: AiAssistantRole | undefined }) {
  const roleColor = role?.color ?? '#6366f1';
  const isDone = stage.total > 0 && stage.done === stage.total;
  return (
    <Stack
      spacing={1}
      alignItems="center"
      sx={{
        flex: '1 1 160px',
        minWidth: 140,
        p: 1.5,
        borderRadius: 'var(--radius-lg)',
        border: '1px solid',
        borderColor: alpha(roleColor, 0.3),
        backgroundColor: alpha(roleColor, 0.06),
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 800,
          color: isDone ? '#fff' : roleColor,
          backgroundColor: isDone ? roleColor : alpha(roleColor, 0.18),
          border: '2px solid',
          borderColor: alpha(roleColor, 0.4),
        }}
      >
        {isDone ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : stage.done}
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.25 }}>
        {stage.name}
      </Typography>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.1,
          py: 0.3,
          borderRadius: 'var(--radius-full)',
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: roleColor,
          backgroundColor: alpha(roleColor, 0.12),
          border: '1px solid',
          borderColor: alpha(roleColor, 0.35),
        }}
      >
        {stage.done} / {stage.total}
      </Box>
    </Stack>
  );
}
